/* BedPeace theme behaviour: cart drawer (with side-aware upsells), kit selector,
   product forms, gallery, sticky add-to-cart, mobile menu, review filters. */
(() => {
  'use strict';

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const cfgEl = $('#bp-config');
  const CFG = cfgEl ? JSON.parse(cfgEl.textContent) : { kits: {}, sides: {}, freeShipping: 7500 };
  const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD';
  const fmt = (cents) => new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(cents / 100);
  const root = CFG.root && CFG.root !== '/' ? CFG.root.replace(/\/$/, '') : '';
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ---------- Header ---------- */
  const header = $('[data-bp-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  const nav = $('[data-bp-drawer-nav]');
  const menuBtn = $('[data-bp-menu-open]');
  if (nav && menuBtn) {
    const setMenu = (open) => {
      nav.classList.toggle('is-open', open);
      menuBtn.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) $('a, button', nav)?.focus();
      else menuBtn.focus();
    };
    menuBtn.addEventListener('click', () => setMenu(true));
    $$('[data-bp-menu-close]', nav).forEach((el) => el.addEventListener('click', () => setMenu(false)));
    nav.addEventListener('keydown', (e) => { if (e.key === 'Escape') setMenu(false); });
  }

  /* ---------- Cart API ---------- */
  const api = {
    get: () => fetch(`${root}/cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
    add: (items) => post(`${root}/cart/add.js`, { items }),
    change: (body) => post(`${root}/cart/change.js`, body),
  };
  async function post(url, body) {
    const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const data = await r.json();
    if (!r.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    return data;
  }

  /* ---------- Cart drawer ---------- */
  const drawer = $('[data-bp-cart]');
  let lastFocus = null;

  function openCart() {
    if (!drawer) return (window.location.href = CFG.cartUrl || '/cart');
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(() => $('.bp-cart__head button', drawer)?.focus(), 50);
  }
  function closeCart() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    lastFocus?.focus?.();
  }
  $$('[data-bp-cart-open]').forEach((a) => a.addEventListener('click', (e) => {
    if (!drawer) return;
    e.preventDefault();
    openCart();
    refreshCart();
  }));
  if (drawer) {
    $$('[data-bp-cart-close]', drawer).forEach((el) => el.addEventListener('click', closeCart));
    drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeCart();
      if (e.key === 'Tab') trapFocus(e, $('.bp-cart__panel', drawer));
    });
  }
  function trapFocus(e, container) {
    const f = $$('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])', container).filter((el) => el.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  async function refreshCart() {
    try { renderCart(await api.get()); } catch (err) { console.error(err); }
  }

  function sidesIn(cart) {
    const handles = cart.items.map((i) => i.handle);
    const has = (list) => (list || []).some((h) => handles.includes(h));
    return {
      handles,
      snorer: has(CFG.sides.snorer),
      partner: has(CFG.sides.partner),
      both: has(CFG.sides.both),
    };
  }

  // Choose one upsell, based on which side(s) of the bed are already covered.
  function pickUpsell(cart) {
    const s = sidesIn(cart);
    const K = CFG.kits || {};
    const inCart = (h) => s.handles.includes(h);
    if (K.complete && K.plus && inCart(K.complete.handle) && !inCart(K.plus.handle)) {
      const line = cart.items.find((i) => i.handle === K.complete.handle);
      return {
        tone: 'plus', kit: K.plus,
        title: 'Upgrade to Complete+',
        text: `90 nights of strips, plus a spare set of QuietPlugs and a DreamMask, for ${fmt(K.plus.price - K.complete.price)} more.`,
        cta: `Upgrade for ${fmt(K.plus.price - K.complete.price)}`,
        swap: line ? line.key : null,
      };
    }
    if (s.both) return null;
    if (s.snorer && !s.partner && K.partner) {
      return { tone: 'partner', kit: K.partner, title: "Don't forget their side of the bed", text: 'QuietPlugs and a DreamMask for the person lying awake.', cta: `Add the Partner Kit, ${fmt(K.partner.price)}` };
    }
    if (s.partner && !s.snorer && K.snorer) {
      return { tone: 'snorer', kit: K.snorer, title: 'Help tackle both sides', text: 'Chin Support and 30 Night Strips for the person snoring.', cta: `Add the Snorer Kit, ${fmt(K.snorer.price)}` };
    }
    return null;
  }

  function renderCart(cart) {
    $$('[data-bp-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.hidden = cart.item_count === 0; });
    if (!drawer) return;
    const body = $('[data-bp-cart-body]', drawer);
    const foot = $('[data-bp-cart-foot]', drawer);
    const ship = $('[data-bp-ship]', drawer);

    // Free shipping progress
    const goal = CFG.freeShipping || 0;
    if (goal > 0 && cart.item_count > 0) {
      const left = goal - cart.total_price;
      const pct = Math.min(100, Math.round((cart.total_price / goal) * 100));
      ship.hidden = false;
      ship.innerHTML = `${left > 0 ? `You're <strong>${fmt(left)}</strong> away from free shipping` : '<strong>Free shipping</strong> unlocked'}
        <div class="bp-cart__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}" aria-label="Progress to free shipping"><span style="width:${pct}%"></span></div>`;
    } else {
      ship.hidden = true;
    }

    if (cart.item_count === 0) {
      foot.hidden = true;
      const k = CFG.kits && CFG.kits.complete;
      body.innerHTML = `<div class="bp-cart__empty"><p>Your cart is empty.</p>
        ${k ? `<a class="btn btn--dark" href="${esc(k.url)}">See the Complete System</a>` : ''}</div>`;
      return;
    }
    foot.hidden = false;
    $('[data-bp-cart-total]', drawer).textContent = fmt(cart.total_price);

    const strips = CFG.strips;
    const lines = cart.items.map((item) => {
      const canSub = strips && strips.plan && item.handle === strips.handle && !item.selling_plan_allocation;
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? `<div class="bp-line__meta">${esc(item.variant_title)}</div>` : '';
      const plan = item.selling_plan_allocation ? `<div class="bp-line__meta">${esc(item.selling_plan_allocation.selling_plan.name)}</div>` : '';
      return `<div class="bp-line" data-key="${esc(item.key)}">
        <a class="bp-line__img" href="${esc(item.url)}" tabindex="-1" aria-hidden="true">${item.image ? `<img src="${esc(item.image + (item.image.includes('?') ? '&' : '?') + 'width=200')}" alt="" loading="lazy" width="200" height="200">` : ''}</a>
        <div>
          <a class="bp-line__title" href="${esc(item.url)}">${esc(item.product_title)}</a>
          ${variantTitle}${plan}
          <div class="bp-qty" role="group" aria-label="Quantity for ${esc(item.product_title)}">
            <button type="button" data-qty="${item.quantity - 1}" aria-label="Decrease quantity">&minus;</button>
            <span>${item.quantity}</span>
            <button type="button" data-qty="${item.quantity + 1}" aria-label="Increase quantity">+</button>
          </div>
          <div><button class="bp-line__remove" type="button" data-qty="0">Remove</button></div>
          ${canSub ? `<button class="bp-line__sub" type="button" data-subscribe>Never run out: switch to monthly refills</button>` : ''}
        </div>
        <div class="bp-line__price">${fmt(item.final_line_price)}</div>
      </div>`;
    }).join('');

    const up = pickUpsell(cart);
    const upsell = up ? `<div class="bp-upsell-card bp-upsell-card--${up.tone}${up.tone === 'plus' ? ' on-night' : ''}">
        <div class="bp-upsell-card__img">${up.kit.image ? `<img src="${esc(up.kit.image)}" alt="" loading="lazy">` : ''}</div>
        <div><h3>${esc(up.title)}</h3><p>${esc(up.text)}</p></div>
        <button class="btn ${up.tone === 'plus' ? 'btn--light' : 'btn--dark'}" type="button" data-upsell="${up.kit.variant}" ${up.swap ? `data-swap="${esc(up.swap)}"` : ''}>${esc(up.cta)}</button>
      </div>` : '';

    body.innerHTML = lines + upsell;
  }

  if (drawer) {
    drawer.addEventListener('click', async (e) => {
      const btn = e.target.closest('button');
      if (!btn || !drawer.contains(btn)) return;
      const line = btn.closest('[data-key]');
      try {
        if (btn.hasAttribute('data-qty') && line) {
          btn.setAttribute('aria-busy', 'true');
          renderCart(await api.change({ id: line.dataset.key, quantity: Number(btn.dataset.qty) }));
        } else if (btn.hasAttribute('data-subscribe') && line) {
          btn.setAttribute('aria-busy', 'true');
          const cart = await api.get();
          const item = cart.items.find((i) => i.key === line.dataset.key);
          if (item) renderCart(await api.change({ id: item.key, quantity: item.quantity, selling_plan: CFG.strips.plan }));
        } else if (btn.hasAttribute('data-upsell')) {
          btn.setAttribute('aria-busy', 'true');
          if (btn.dataset.swap) await api.change({ id: btn.dataset.swap, quantity: 0 });
          await api.add([{ id: Number(btn.dataset.upsell), quantity: 1 }]);
          await refreshCart();
        }
      } catch (err) {
        alert(err.message);
        refreshCart();
      }
    });
  }

  /* ---------- Add-to-cart forms ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-bp-add]');
    if (!form || !drawer) return;
    e.preventDefault();
    const submitter = e.submitter || $('[type="submit"]', form) || $(`[form="${form.id}"][type="submit"]`);
    const scope = form.closest('.bp-buy');
    const error = $('[data-bp-error]', form) || (scope && $('[data-bp-error]', scope));
    const fd = new FormData(form);
    const item = { id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) };
    const plan = fd.get('selling_plan');
    if (plan) item.selling_plan = Number(plan);
    try {
      submitter?.setAttribute('aria-busy', 'true');
      if (error) error.hidden = true;
      await api.add([item]);
      await refreshCart();
      openCart();
    } catch (err) {
      if (error) { error.textContent = err.message; error.hidden = false; } else alert(err.message);
    } finally {
      submitter?.removeAttribute('aria-busy');
    }
  });

  /* ---------- Kit selector (kit product page) ---------- */
  const kitPage = $('[data-bp-kit-page]');
  if (kitPage) {
    const radios = $$('input[name="kit"]', kitPage);
    const show = (attr, handle) => $$(`[${attr}]`).forEach((el) => { el.hidden = el.getAttribute(attr) !== handle; });
    radios.forEach((r) => r.addEventListener('change', () => {
      if (!r.checked) return;
      const k = JSON.parse(r.dataset.kit);
      show('data-kit-gallery', k.handle);
      show('data-kit-sub', k.handle);
      show('data-kit-list', k.handle);
      $$('[data-kit-title]').forEach((el) => { el.textContent = k.title; });
      $$('[data-kit-price]').forEach((el) => { el.textContent = fmt(k.price); });
      const sep = $('[data-kit-sep]', kitPage);
      const save = $('[data-kit-save]', kitPage);
      if (k.save > 0) {
        sep.textContent = `${fmt(k.sep)} bought separately`;
        save.textContent = `You save ${fmt(k.save)}`;
        save.hidden = false;
      } else {
        sep.textContent = '';
        save.hidden = true;
      }
      $('[data-kit-variant]', kitPage).value = k.variant;
      if (history.replaceState) history.replaceState({}, '', k.url);
      document.title = document.title.replace(/^[^–-]+/, `${k.title} `);
    }));
  }

  /* ---------- Variant pills (single product page) ---------- */
  $$('input[name="bp-variant"]').forEach((r) => r.addEventListener('change', () => {
    if (!r.checked) return;
    const form = $('[data-bp-main-atc]');
    if (form) $('[data-variant-id]', form).value = r.value;
    $$('[data-price]').forEach((el) => { if (!el.matches('input')) el.textContent = r.dataset.price; });
    $$('[data-once-price]').forEach((el) => { el.textContent = r.dataset.price; });
    $$('[data-plan-price]').forEach((el) => {
      if (el.matches('input')) return;
      if (r.dataset.planPrice) el.innerHTML = `${esc(r.dataset.planPrice)}<s>${esc(r.dataset.price)}</s>`;
    });
    if (history.replaceState && r.dataset.url) history.replaceState({}, '', r.dataset.url);
  }));

  /* ---------- Gallery thumbnails ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-thumb]');
    if (!t) return;
    const scope = t.closest('[data-kit-gallery]') || t.closest('.bp-gallery');
    const main = $('[data-gallery-main]', scope);
    if (!main) return;
    main.innerHTML = `<img src="${esc(t.dataset.thumb)}" alt="${esc(t.dataset.alt)}">`;
    $$('[data-thumb]', scope).forEach((b) => b.setAttribute('aria-current', String(b === t)));
  });

  /* ---------- Sticky add to cart ---------- */
  const sticky = $('[data-bp-sticky]');
  const mainAtc = $('[data-bp-main-atc]');
  if (sticky && mainAtc && 'IntersectionObserver' in window) {
    const target = $('.bp-buy__atc') || mainAtc;
    new IntersectionObserver(([entry]) => {
      const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
      $('button', sticky).tabIndex = visible ? 0 : -1;
    }).observe(target);
  }

  /* ---------- Review filters ---------- */
  $$('[data-bp-reviews]').forEach((sec) => {
    const chips = $$('[data-filter]', sec);
    const reviews = $$('.bp-review', sec);
    const more = $('[data-bp-more]', sec);
    let expanded = false;
    const apply = (f) => {
      reviews.forEach((r) => {
        const match = f === 'all' || r.dataset.side === f;
        r.hidden = !match || (f === 'all' && !expanded && r.hasAttribute('data-more'));
      });
    };
    chips.forEach((c) => c.addEventListener('click', () => {
      chips.forEach((x) => x.setAttribute('aria-pressed', String(x === c)));
      apply(c.dataset.filter);
    }));
    more?.addEventListener('click', () => { expanded = true; apply('all'); more.remove(); });
  });

  /* Keep cart count fresh when returning via back/forward cache */
  window.addEventListener('pageshow', (e) => { if (e.persisted) refreshCart(); });
  if (drawer) refreshCart();
})();
